import { useSetContainerValues } from '@/components/container/context/context';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { formatDate } from '@/lib';
import { type Device } from '@/portal-api.definition';
import { ChevronRight, Info, Loader, Lock, Tv } from '@nosinovacao/fe-icons';
import {
  ActionCard,
  Alert,
  Button,
  Checkbox,
  cn,
  If,
  Sheet,
  Tip,
  useI18n,
  useInMediaQuery,
} from '@nosinovacao/nosid-mfe-common';
import { Link, Outlet } from '@tanstack/react-router';
import { useState } from 'react';
import { DeviceIconMap } from '../-model/model';
import { type TvProps } from '../-model/props';

export function TvLight({
  devices,
  isLoadingDevices,
  sa,
  deleteDevice,
  deleteDeviceLoading,
  canEditPins,
  canEditPinsLoading,
}: Readonly<TvProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Tv.Title'),
    subtitle: t('Tv.Subtitle'),
  });
  return (
    <>
      <main className="grid gap-6 desktop-md:grid-cols-[1fr_minmax(16rem,max-content)] desktop-lg:grid-cols-[1fr_minmax(21.25rem,max-content)]">
        <SkeletonLight loading={isLoadingDevices} className="h-[200px] w-full rounded-lg">
          <DevicesList
            sa={sa}
            devices={devices ?? []}
            deleteDevice={deleteDevice}
            deleteDeviceLoading={deleteDeviceLoading}
          />
        </SkeletonLight>
        <div className="flex flex-col gap-2">
          <h2
            data-tid="title-tv-options"
            className="font-light text-nos-neutral-900 font-text-p1 desktop-md:sr-only"
          >
            {t('Tv.TvOptions.Title')}
          </h2>
          <ActionCard
            data-tid="card-device-management"
            skin="light"
            variant="quick-link"
            preTitle={t('Tv.TvOptions.Manage')}
            subTitle={<></>}
            title={t('Tv.TvOptions.Devices')}
            icon={<ChevronRight width={20} height={20} />}
            className="w-full bg-nos-neutral-0 shadow-1"
            asChild
          >
            <Link
              to="/tv/$id/device-management"
              params={{ id: sa }}
              search={{ redirectOnDelete: false, device_name: null, device_type: null }}
            />
          </ActionCard>
          <ActionCard
            data-tid="card-tv-profiles"
            skin="light"
            variant="quick-link"
            preTitle={t('Tv.TvOptions.Manage')}
            subTitle={<></>}
            title={t('Tv.TvOptions.TvProfiles')}
            icon={<ChevronRight width={20} height={20} />}
            className="w-full bg-nos-neutral-0 shadow-1"
            asChild
          >
            <Link to="/tv/$id/profiles" params={{ id: sa }} />
          </ActionCard>
          <SkeletonLight loading={canEditPinsLoading} className="h-24">
            <If condition={canEditPins}>
              <ActionCard
                data-tid="card-pins"
                skin="light"
                variant="quick-link"
                preTitle={t('Tv.TvOptions.Manage')}
                subTitle={<></>}
                title={t('Tv.TvOptions.Pins')}
                icon={<ChevronRight width={20} height={20} />}
                className="w-full bg-nos-neutral-0 shadow-1"
                asChild
              >
                <Link to="/tv/$id/pins" params={{ id: sa }} />
              </ActionCard>
            </If>
          </SkeletonLight>
        </div>
      </main>
      <Outlet />
    </>
  );
}

function DevicesList({
  devices,
  deleteDevice,
  deleteDeviceLoading,
  sa,
}: Readonly<{ devices: Device[] } & Pick<TvProps, 'deleteDevice' | 'deleteDeviceLoading' | 'sa'>>) {
  const { t } = useI18n();
  const isTablet = useInMediaQuery('tablet');
  if (!devices.length)
    return (
      <div className="grid h-[200px] w-full place-items-center">
        <span data-tid="text-no-devices" className="font-medium text-nos-neutral-900 font-text-p1">
          {t('Tv.NoDevices')}
        </span>
      </div>
    );

  if (isTablet)
    return (
      <div className="grid grid-cols-2 gap-2">
        {devices.map((device) => (
          <DeviceCard
            device={device}
            key={device.Id}
            deleteDevice={deleteDevice}
            deleteDeviceLoading={deleteDeviceLoading}
          />
        ))}
        <DeviceLimitInfo id={sa} />
      </div>
    );

  return (
    <Carousel>
      <CarouselContent wrapperClassName="-mx-4 w-dvw">
        {devices.map((device, index) => (
          <CarouselItem
            data-first={!index}
            className="h-[12.5rem] basis-[min(21rem,90%)] data-[first=false]:pl-2 data-[first=true]:pl-4"
            key={device.Id}
          >
            <DeviceCard
              device={device}
              deleteDevice={deleteDevice}
              deleteDeviceLoading={deleteDeviceLoading}
            />
          </CarouselItem>
        ))}
        <CarouselItem className="h-[12.5rem] basis-[min(21rem,90%)] pl-2 pr-4">
          <DeviceLimitInfo id={sa} />
        </CarouselItem>
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  );
}

function DeviceCard({
  device,
  className,
  deleteDevice,
  deleteDeviceLoading,
}: Readonly<
  { device: Device; className?: string } & Pick<TvProps, 'deleteDevice' | 'deleteDeviceLoading'>
>) {
  const { t } = useI18n();
  const isTablet = useInMediaQuery('tablet');
  const DeviceIcon = DeviceIconMap[device.Type] ?? Tv;
  const [canDeleteDevice, setCanDeleteDevice] = useState(false);
  const name = device.Name || t(`Tv.Device.DefaultName.${device.Type}`);
  return (
    <Sheet
      onOpenChange={(o) => {
        if (!o) setCanDeleteDevice(false);
      }}
      trigger={
        <button
          data-tid="button-device-card"
          type="button"
          className={cn(
            'relative grid w-full gap-6 rounded-lg border border-nos-neutral-100 bg-nos-neutral-0 p-4 shadow-1',
            className,
          )}
        >
          {device.IsLocked ? (
            <Lock width={24} height={24} className="absolute right-4 top-4 text-nos-red" />
          ) : null}
          <div className="grid w-full place-items-center gap-2 text-nos-neutral-1000">
            <DeviceIcon width={48} height={48} />
            <span data-tid="text-device-name" className="font-medium font-text-p1">
              {name}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 pb-4">
            {[
              { date: device.FirstAccess, label: 'Tv.Device.FirstAccess' },
              { date: device.LastAccess, label: 'Tv.Device.LastAccess' },
            ].map((access) => (
              <div key={access.label} className="grid place-items-center gap-1">
                <span className="text-nos-neutral-500 font-text-p2">{t(access.label)}</span>
                <span className="text-nos-neutral-900 font-text-p1">{formatDate(access.date)}</span>
              </div>
            ))}
          </div>
        </button>
      }
      skin="light"
      overlayClassName="z-[60]"
      title={t('DeviceManagement.SingleTitle')}
      animationDirection={isTablet ? 'right' : 'bottom'}
      className="z-[60] flex flex-col gap-4"
    >
      <div className="grid gap-1">
        <span className="text-nos-neutral-500 font-text-p2">{t('Tv.Device.Name')}</span>
        <span className="font-medium text-nos-neutral-900 font-text-p1">{name}</span>
      </div>
      <div className="grid gap-1">
        <span className="text-nos-neutral-500 font-text-p2">{t('Tv.Device.FirstAccess')}</span>
        <span className="font-medium text-nos-neutral-900 font-text-p1">
          {formatDate(device.FirstAccess)}
        </span>
      </div>
      <div className="grid gap-1">
        <span className="text-nos-neutral-500 font-text-p2">{t('Tv.Device.LastAccess')}</span>
        <span className="font-medium text-nos-neutral-900 font-text-p1">
          {formatDate(device.LastAccess)}
        </span>
      </div>
      <div className="mt-auto grid gap-8 max-tablet:pb-8">
        <If condition={device.IsLocked}>
          <Tip
            skin="light"
            title={t('DeviceManagement.LockedTip.Title', {
              date: formatDate(device.LockExpireDate),
            })}
            description={t('DeviceManagement.LockedTip.Description')}
            type="warning"
            hasIcon
            customIcon={Lock}
          ></Tip>
        </If>
        <div className="grid gap-6">
          <Checkbox
            data-tid="checkbox-delete-device"
            id="delete-device"
            skin="light"
            disabled={device.IsLocked}
            checked={canDeleteDevice}
            onChange={() => setCanDeleteDevice((v) => !v)}
            label={t('DeviceManagement.DeleteDeviceCheckboxLabel')}
          />
          <Button
            data-tid="button-delete-device"
            skin="light"
            destructive
            type="button"
            onClick={() => {
              deleteDevice(device);
            }}
            disabled={device.IsLocked || !canDeleteDevice || deleteDeviceLoading}
          >
            <If
              condition={deleteDeviceLoading}
              otherwise={t('DeviceManagement.DeleteDeviceButton')}
            >
              <Loader width={24} height={24} />
            </If>
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function DeviceLimitInfo({ id }: Readonly<{ id: string }>) {
  const { t } = useI18n();
  return (
    <div className="grid w-full place-items-center gap-4 p-4">
      <div className="grid w-fit place-items-center rounded-lg bg-nos-neutral-0 px-1 py-1.5 text-nos-blue shadow-1">
        <Info width={40} height={40} />
      </div>
      <span className="text-center font-medium font-text-p1">
        {t('Tv.Device.Info.Banner.Description')}
      </span>
      <Alert
        data-tid="alert-device-info"
        skin="light"
        icon={<Info width={48} height={48} className="text-nos-blue" />}
        title={t('Tv.Device.Info.Banner.Alert.Title')}
        description={t('Tv.Device.Info.Banner.Alert.Description')}
        className="z-[60]"
        overlayClassName="z-[60]"
        buttons={(close) => (
          <div className="mt-10 grid w-full gap-4 tablet:grid-cols-2">
            <Button
              data-tid="button-manage-devices"
              skin="light"
              variant="secondary"
              type="button"
              onClick={close}
              asChild
            >
              <Link
                to="/tv/$id/device-management"
                params={{ id }}
                search={{ redirectOnDelete: false, device_name: null, device_type: null }}
              >
                {t('Tv.Device.Info.Banner.Alert.ManagerDevices')}
              </Link>
            </Button>
            <Button data-tid="button-close-device-info" skin="light" type="button" onClick={close}>
              {t('Tv.Device.Info.Banner.Alert.Close')}
            </Button>
          </div>
        )}
      >
        <Button data-tid="button-device-info" skin="light" variant="tertiary" type="button">
          {t('Tv.Device.Info.Banner.Button')}
        </Button>
      </Alert>
    </div>
  );
}
